/**
 * Ausilium Usato – Shopify Theme JS
 * Converted from React app (Zustand + Storefront API)
 * Replaced with Shopify AJAX Cart API + vanilla JS
 */

'use strict';

/* ==========================================================================
   1. CART MODULE
   ========================================================================== */

const Cart = (() => {
  let state = null; // Cached /cart.js response

  /* ---- Fetch cart from Shopify ----------------------------------------- */
  async function fetch() {
    const res = await window.fetch('/cart.js');
    if (!res.ok) throw new Error('Cart fetch failed');
    state = await res.json();
    return state;
  }

  /* ---- Add item to cart ------------------------------------------------- */
  async function addItem(variantId, quantity = 1) {
    const res = await window.fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Impossibile aggiungere al carrello');
    }
    await refresh();
  }

  /* ---- Change a line item (index is 1-based) ----------------------------- */
  async function changeLine(line, quantity) {
    const res = await window.fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    if (!res.ok) throw new Error('Cart update failed');
    state = await res.json();
    UI.renderItems(state);
    UI.updateBadge(state.item_count);
    UI.updateTotal(state.total_price);
    UI.updateFooterVisibility(state.item_count);
    UI.updateSubtitle(state.item_count);
  }

  /* ---- Refresh cart and re-render --------------------------------------- */
  async function refresh() {
    state = await fetch();
    UI.renderItems(state);
    UI.updateBadge(state.item_count);
    UI.updateTotal(state.total_price);
    UI.updateFooterVisibility(state.item_count);
    UI.updateSubtitle(state.item_count);
  }

  /* ---- Public ----------------------------------------------------------- */
  return { fetch, addItem, changeLine, refresh, getState: () => state };
})();

/* ==========================================================================
   2. CART UI MODULE
   ========================================================================== */

const UI = (() => {
  const $ = (id) => document.getElementById(id);

  /* ---- Format money (Shopify prices are in cents) ----------------------- */
  function formatMoney(cents) {
    return '€\u00a0' + (cents / 100).toFixed(2).replace('.', ',');
  }

  /* ---- Update cart badge count ----------------------------------------- */
  function updateBadge(count) {
    const badge = $('cart-badge');
    if (!badge) return;
    if (count > 0) {
      badge.textContent = count > 99 ? '99+' : count;
      badge.removeAttribute('hidden');
    } else {
      badge.setAttribute('hidden', '');
    }
  }

  /* ---- Update total price ---------------------------------------------- */
  function updateTotal(totalCents) {
    const el = $('cart-total-price');
    if (el) el.textContent = formatMoney(totalCents);
  }

  /* ---- Show/hide footer ------------------------------------------------- */
  function updateFooterVisibility(count) {
    const footer = $('cart-footer');
    const empty  = $('cart-empty');
    const list   = $('cart-items-list');
    if (!footer) return;
    if (count > 0) {
      footer.removeAttribute('hidden');
      if (empty) empty.style.display = 'none';
      if (list)  list.style.display = '';
    } else {
      footer.setAttribute('hidden', '');
      if (empty) empty.style.display = '';
      if (list)  list.style.display = 'none';
    }
  }

  /* ---- Update subtitle -------------------------------------------------- */
  function updateSubtitle(count) {
    const el = $('cart-subtitle');
    if (!el) return;
    if (count === 0) {
      el.textContent = 'Il tuo carrello è vuoto';
    } else {
      el.textContent = count === 1 ? '1 articolo nel carrello' : `${count} articoli nel carrello`;
    }
  }

  /* ---- Render cart items ------------------------------------------------ */
  function renderItems(cart) {
    const list = $('cart-items-list');
    if (!list) return;

    if (!cart.items || cart.items.length === 0) {
      list.innerHTML = '';
      return;
    }

    list.innerHTML = cart.items.map((item, index) => {
      const lineIndex = index + 1; // Shopify lines are 1-based
      const imageHtml = item.image
        ? `<img src="${item.image}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
        : '';
      const optionsHtml = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-item__options">${escapeHtml(item.variant_title)}</p>`
        : '';

      return `
        <li class="cart-item" data-line="${lineIndex}">
          <div class="cart-item__image">${imageHtml}</div>
          <div class="cart-item__info">
            <p class="cart-item__title">${escapeHtml(item.product_title)}</p>
            ${optionsHtml}
            <p class="cart-item__price">${formatMoney(item.final_price)}</p>
          </div>
          <div class="cart-item__controls">
            <button class="remove-btn" data-action="remove" data-line="${lineIndex}"
                    aria-label="Rimuovi ${escapeHtml(item.product_title)}">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 6h18"/>
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
              </svg>
            </button>
            <div class="quantity-control">
              <button class="quantity-btn" data-action="decrement" data-line="${lineIndex}"
                      data-current="${item.quantity}" aria-label="Diminuisci quantità">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2">
                  <path d="M5 12h14"/>
                </svg>
              </button>
              <span>${item.quantity}</span>
              <button class="quantity-btn" data-action="increment" data-line="${lineIndex}"
                      data-current="${item.quantity}" aria-label="Aumenta quantità">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2">
                  <path d="M5 12h14"/><path d="M12 5v14"/>
                </svg>
              </button>
            </div>
          </div>
        </li>
      `;
    }).join('');

    // Attach quantity/remove event listeners
    list.querySelectorAll('[data-action]').forEach((btn) => {
      btn.addEventListener('click', handleCartItemAction);
    });
  }

  async function handleCartItemAction(e) {
    const btn = e.currentTarget;
    const action  = btn.dataset.action;
    const line    = parseInt(btn.dataset.line, 10);
    const current = parseInt(btn.dataset.current || '1', 10);

    btn.disabled = true;
    try {
      if (action === 'remove' || (action === 'decrement' && current <= 1)) {
        await Cart.changeLine(line, 0);
      } else if (action === 'decrement') {
        await Cart.changeLine(line, current - 1);
      } else if (action === 'increment') {
        await Cart.changeLine(line, current + 1);
      }
    } catch (err) {
      Toast.show('Errore', err.message, 'error');
    } finally {
      btn.disabled = false;
    }
  }

  /* ---- Helper: escape HTML -------------------------------------------- */
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  return { updateBadge, updateTotal, updateFooterVisibility, updateSubtitle, renderItems, formatMoney };
})();

/* ==========================================================================
   3. CART DRAWER MODULE
   ========================================================================== */

const CartDrawer = (() => {
  const drawer  = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-overlay');
  const toggle  = document.getElementById('cart-toggle');
  const close   = document.getElementById('cart-close');
  let isOpen = false;

  function open() {
    if (!drawer) return;
    isOpen = true;
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    close && close.focus();
  }

  function closeFn() {
    if (!drawer) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    toggle && toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    toggle && toggle.focus();
  }

  function init() {
    toggle  && toggle.addEventListener('click', async () => {
      open();
      try { await Cart.refresh(); } catch(e) {}
    });
    close   && close.addEventListener('click', closeFn);
    overlay && overlay.addEventListener('click', closeFn);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) closeFn();
    });
  }

  return { init, open, close: closeFn };
})();

/* ==========================================================================
   4. ADD TO CART HANDLERS
   ========================================================================== */

function initAddToCartButtons() {
  // Product cards on collection / home pages
  document.querySelectorAll('.add-to-cart-card').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const variantId = btn.dataset.variantId;
      const title     = btn.dataset.productTitle || 'Prodotto';
      if (!variantId) return;

      btn.disabled = true;
      const originalHtml = btn.innerHTML;
      btn.innerHTML = '<span class="spinner"></span>';

      try {
        await Cart.addItem(parseInt(variantId, 10));
        Toast.show('Aggiunto al carrello', title, 'success');
        CartDrawer.open();
      } catch (err) {
        Toast.show('Errore', err.message, 'error');
      } finally {
        btn.innerHTML = originalHtml;
        btn.disabled = false;
      }
    });
  });

  // Product detail form
  const productForm = document.getElementById('product-form');
  if (productForm) {
    productForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const variantIdInput = document.getElementById('variant-id');
      if (!variantIdInput) return;
      const variantId = parseInt(variantIdInput.value, 10);
      const btn       = document.getElementById('add-to-cart-btn');

      if (btn) {
        btn.disabled = true;
        btn.innerHTML = '<span class="spinner"></span> Aggiunta...';
      }
      try {
        await Cart.addItem(variantId);
        Toast.show('Aggiunto al carrello', document.querySelector('.product-title')?.textContent || 'Prodotto', 'success');
        CartDrawer.open();
      } catch (err) {
        Toast.show('Errore', err.message, 'error');
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/>
              <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
            </svg>
            Aggiungi al Carrello
          `;
        }
      }
    });
  }
}

/* ==========================================================================
   5. PRODUCT IMAGE GALLERY
   ========================================================================== */

function initProductGallery() {
  const thumbnailsWrap = document.getElementById('thumbnails-wrap');
  if (!thumbnailsWrap) return;

  const mainImg = document.getElementById('main-product-image');

  thumbnailsWrap.querySelectorAll('.thumbnail').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      // Update main image
      if (mainImg) {
        mainImg.src = thumb.dataset.src;
        mainImg.alt = thumb.dataset.alt || '';
      }
      // Update active thumbnail
      thumbnailsWrap.querySelectorAll('.thumbnail').forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });
}

/* ==========================================================================
   6. VARIANT SELECTOR (Product page) – select dropdowns + button groups
   ========================================================================== */

function initVariantSelector() {
  if (!window.productVariants) return;

  const variantIdInput = document.getElementById('variant-id');
  const priceEl        = document.getElementById('product-price');
  const skuEl          = document.getElementById('product-sku');
  const addBtn         = document.getElementById('add-to-cart-btn');

  // Collect selected options from BOTH selects and variant-btn groups
  function getSelectedOptions() {
    const options = [];

    // Select dropdowns
    document.querySelectorAll('[data-option-index]').forEach((el) => {
      const idx = parseInt(el.dataset.optionIndex, 10);
      if (el.tagName === 'SELECT') {
        options[idx] = el.value;
      }
    });

    // Button groups: find active button per group
    document.querySelectorAll('.variant-btn-group').forEach((group) => {
      const activeBtn = group.querySelector('.variant-btn.is-active');
      const idx = parseInt(group.querySelector('.variant-btn')?.dataset.optionIndex ?? '0', 10);
      if (activeBtn) options[idx] = activeBtn.dataset.value;
    });

    return options;
  }

  function getSelectedVariant() {
    const selectedOptions = getSelectedOptions();
    return window.productVariants.find(v =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );
  }

  function updateVariantUI(variant) {
    if (!variant) return;

    // Update hidden input
    if (variantIdInput) variantIdInput.value = variant.id;

    // Update price (handle compare_at_price)
    if (priceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceEl.innerHTML =
          `<span class="product-price--sale">${UI.formatMoney(variant.price)}</span>` +
          `<s class="product-price--compare">${UI.formatMoney(variant.compare_at_price)}</s>`;
      } else {
        priceEl.textContent = UI.formatMoney(variant.price);
      }
    }

    // Update SKU
    if (skuEl) skuEl.textContent = variant.sku || '';

    // Update add-to-cart button
    if (addBtn) {
      addBtn.disabled = !variant.available;
      const cartIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>`;
      addBtn.innerHTML = variant.available
        ? `${cartIcon} Aggiungi al Carrello`
        : 'Prodotto non disponibile';
    }

    // Update URL without reload
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  // Listen to select dropdowns
  document.querySelectorAll('select[data-option-index]').forEach(s => {
    s.addEventListener('change', () => updateVariantUI(getSelectedVariant()));
  });

  // Listen to variant buttons
  document.querySelectorAll('.variant-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const group = btn.closest('.variant-btn-group');
      group.querySelectorAll('.variant-btn').forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      updateVariantUI(getSelectedVariant());
    });
  });
}

/* ==========================================================================
   6b. COLLAPSIBLE ACCORDION
   ========================================================================== */

function initCollapsibles() {
  document.querySelectorAll('.collapsible-trigger').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      const bodyId   = trigger.getAttribute('aria-controls');
      const body     = bodyId ? document.getElementById(bodyId) : trigger.nextElementSibling;
      if (!body) return;

      trigger.setAttribute('aria-expanded', !expanded);
      body.classList.toggle('collapsible-body--closed', expanded);
    });
  });
}

/* ==========================================================================
   7. COLLECTION SEARCH & CATEGORY FILTERS
   ========================================================================== */

function initCollectionFilters() {
  const searchInput = document.getElementById('product-search');
  const grid        = document.getElementById('products-grid');
  const searchEmpty = document.getElementById('search-empty');
  const filterBtns  = document.querySelectorAll('.filter-btn');

  if (!grid) return;

  const allCards = Array.from(grid.querySelectorAll('.product-card'));
  let activeCategory = '';

  function filterProducts() {
    const query = searchInput ? searchInput.value.trim().toLowerCase() : '';
    let visibleCount = 0;

    allCards.forEach((card) => {
      const title    = card.dataset.productTitle || '';
      const desc     = card.dataset.productDesc  || '';
      const type     = card.dataset.productType  || '';

      const matchesSearch = !query ||
        title.includes(query) ||
        desc.includes(query);

      const matchesCategory = !activeCategory ||
        type.toLowerCase() === activeCategory.toLowerCase();

      const visible = matchesSearch && matchesCategory;
      card.style.display = visible ? '' : 'none';
      if (visible) visibleCount++;
    });

    // Show/hide empty state
    if (searchEmpty) {
      if (visibleCount === 0) {
        searchEmpty.removeAttribute('hidden');
      } else {
        searchEmpty.setAttribute('hidden', '');
      }
    }
  }

  // Search input
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      clearTimeout(searchInput._debounce);
      searchInput._debounce = setTimeout(filterProducts, 200);
    });
  }

  // Category filter buttons
  filterBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      activeCategory = btn.dataset.category || '';
      filterBtns.forEach(b => {
        b.classList.toggle('is-active', b === btn);
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
      filterProducts();
    });
  });
}

/* ==========================================================================
   8. MOBILE MENU
   ========================================================================== */

function initMobileMenu() {
  const toggle = document.getElementById('mobile-menu-toggle');
  const nav    = document.getElementById('mobile-nav');
  if (!toggle || !nav) return;

  toggle.addEventListener('click', () => {
    const open = nav.classList.toggle('is-open');
    toggle.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    nav.setAttribute('aria-hidden', open ? 'false' : 'true');
  });

  // Close when a link is clicked
  nav.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', () => {
      nav.classList.remove('is-open');
      toggle.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      nav.setAttribute('aria-hidden', 'true');
    });
  });
}

/* ==========================================================================
   9. TOAST NOTIFICATIONS
   ========================================================================== */

const Toast = (() => {
  const container = document.getElementById('toast-container');

  function show(title, desc = '', type = 'success') {
    if (!container) return;

    const icon = type === 'success'
      ? `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="toast__icon">
           <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/>
         </svg>`
      : `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="toast__icon">
           <circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/>
           <line x1="12" x2="12.01" y1="16" y2="16"/>
         </svg>`;

    const el = document.createElement('div');
    el.className = `toast toast--${type}`;
    el.innerHTML = `
      ${icon}
      <div class="toast__content">
        <p class="toast__title">${title}</p>
        ${desc ? `<p class="toast__desc">${desc}</p>` : ''}
      </div>
    `;

    container.appendChild(el);
    setTimeout(() => {
      el.classList.add('toast-out');
      el.addEventListener('animationend', () => el.remove(), { once: true });
    }, 3500);
  }

  return { show };
})();

/* ==========================================================================
   10. INIT
   ========================================================================== */

document.addEventListener('DOMContentLoaded', async () => {
  // Init UI modules
  CartDrawer.init();
  initMobileMenu();
  initAddToCartButtons();
  initProductGallery();
  initVariantSelector();
  initCollapsibles();
  initCollectionFilters();

  // Load initial cart state (badge count)
  try {
    const cart = await Cart.fetch();
    UI.updateBadge(cart.item_count);
  } catch (e) {
    // Silently fail if cart fetch errors
  }
});
